import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const MAX_COMPARE_ITEMS = 2;

/**
 * @typedef {object} SizeChartRefs
 * @property {HTMLScriptElement} data – The JSON data script element.
 * @property {HTMLButtonElement} unitToggle – The CM/IN toggle switch button.
 */

/**
 * A custom element that manages size selection and unit toggle within a size chart dialog.
 *
 * @extends Component<SizeChartRefs>
 */
export default class SizeChart extends Component {
  /** @type {string[]} */
  #fields = [];

  /** @type {Set<string>} */
  #noUnitFields = new Set();

  /** @type {{ measurements: { fields: string[], unit_excluded_fields?: string[], cm: { sizes: object[] }, in: { sizes: object[] } } } | null} */
  #data = null;

  /** @type {'cm' | 'in'} */
  #currentUnit = 'cm';

  /** @type {number[]} */
  #selectedIndices = [];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {HTMLButtonElement[]} */
  #sizeButtons = [];

  /** @type {HTMLTableCellElement[]} */
  #headerCells = [];

  /** @type {Map<string, HTMLTableCellElement[]>} */
  #bodyCells = new Map();

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#data = JSON.parse(this.refs.data?.textContent ?? '');
    } catch {
      console.error('SizeChart: failed to parse size chart data');
    }

    if (this.#data && (
        !this.#data.measurements?.fields ||
        !this.#data.measurements?.cm?.sizes ||
        !this.#data.measurements?.in?.sizes
    )) {
      console.error('SizeChart: invalid data structure', this.#data);
      this.#data = null;
    }

    this.#fields = this.#data?.measurements?.fields ?? [];
    this.#noUnitFields = new Set(this.#data?.measurements?.unit_excluded_fields ?? []);

    this.#cacheTableElements();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', this.#handleSizeClick, { signal });
    this.refs.unitToggle?.addEventListener('click', this.#handleUnitToggle, { signal });

    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    this.#preselectCurrentSize();
  }

  /**
   * Caches references to DOM elements used during rendering to avoid repeated queries.
   */
  #cacheTableElements() {
    this.#sizeButtons = Array.from(this.querySelectorAll('.size-chart__size-item'));
    this.#headerCells = Array.from(this.querySelectorAll('thead th[data-col-index]'));

    for (const field of this.#fields) {
      this.#bodyCells.set(
        field,
        Array.from(this.querySelectorAll(`[data-row="${field}"] td[data-col-index]`))
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * Re-caches DOM references and restores active state after a variant update re-renders the DOM.
   */
  #handleVariantUpdate = () => {
    this.#preselectCurrentSize();
  };

  /**
   * Finds the currently selected variant size on the page and pre-selects
   * the matching size button in the size chart.
   */
  #preselectCurrentSize() {
    this.#currentUnit = 'cm';
    this.refs.unitToggle?.setAttribute('aria-checked', 'false');

    if (this.#sizeButtons.length === 1) {
      this.#selectSize(0);
      return;
    }

    const checkedInput = this.closest('[data-fieldset-index]')?.querySelector('input[type="radio"]:checked');
    const selectedSize = (checkedInput?.value ?? this.closest('[data-selected-size]')?.dataset.selectedSize)?.trim();

    const matchingButton = this.#sizeButtons.find(
      (btn) => btn.dataset.size?.trim().toLowerCase() === selectedSize?.toLowerCase()
    ) ?? this.#sizeButtons[0];

    if (!matchingButton) return;

    this.#selectSize(parseInt(matchingButton.dataset.sizeIndex ?? '0', 10));
  }

  /**
   * Sets the selected index directly without moving focus.
   * @param {number} index - The size index to select.
   */
  #selectSize(index) {
    this.#selectedIndices = [index];
    this.#syncSelectedState();
    this.#renderTable();
  }

  /**
   * Handles a click on a size button.
   * Toggles selection: deselects if already selected, otherwise adds to the comparison (max 2).
   * If 2 are already selected, replaces the oldest one.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleSizeClick = (event) => {
    if (!(event.target instanceof HTMLButtonElement)) return;

    const indexAttr = event.target.dataset.sizeIndex;
    if (indexAttr === undefined) return;
    const index = parseInt(indexAttr, 10);

    const existingPosition = this.#selectedIndices.indexOf(index);

    if (existingPosition !== -1 && this.#sizeButtons.length > 1) {
      this.#selectedIndices.splice(existingPosition, 1);
    } else if (existingPosition === -1 && this.#selectedIndices.length < MAX_COMPARE_ITEMS) {
      this.#selectedIndices.unshift(index);
    } else {
      // Replace the oldest selected size when the limit is reached
      this.#selectedIndices.pop();
      this.#selectedIndices.unshift(index);
    }

    this.#syncSelectedState();
    this.#renderTable();
  };

  /**
   * Syncs the active state and aria-pressed attribute of all size buttons
   * to match the current selection.
   */
  #syncSelectedState() {
    const selectedSet = new Set(this.#selectedIndices);
    this.#sizeButtons.forEach((el) => {
      const isSelected = selectedSet.has(parseInt(el.dataset.sizeIndex ?? '', 10));
      el.classList.toggle('is-active', isSelected);
      el.setAttribute('aria-pressed', String(isSelected));
    });
  }

  /**
   * Handles the unit toggle click event.
   */
  #handleUnitToggle = () => {
    const toggle = this.refs.unitToggle;
    const isInUnit = toggle.getAttribute('aria-checked') === 'true';
    const nextState = !isInUnit;

    toggle.setAttribute('aria-checked', String(nextState));

    this.#currentUnit = nextState ? 'in' : 'cm';
    this.#renderTable();
  };

  /**
   * Renders the comparison table for the currently selected sizes and unit.
   * Clears all columns first, then fills each column based on selected sizes.
   */
  #renderTable() {
    const sizes = this.#data?.measurements[this.#currentUnit]?.sizes;
    if (!sizes) return;
    const orderedSelectedIndices = Array.from(this.#selectedIndices).sort((a, b) => a - b);

    this.#headerCells.forEach((th) => { th.textContent = ''; });
    this.#bodyCells.forEach((cells) => cells.forEach((td) => { td.textContent = ''; }));

    orderedSelectedIndices.forEach((sizeIndex, colIndex) => {
      const selected = sizes[sizeIndex];
      if (!selected) return;

      const sizeLabel = String(selected.size ?? ''),
          toFitLabel = String(selected.to_fit ?? '').trim().toUpperCase(),
          fitShortLabel = this.dataset.fitShortLabel,
          fitLongLabel = this.dataset.fitLongLabel;

      const fitSuffix = toFitLabel && toFitLabel !== sizeLabel.toUpperCase()
        ? (toFitLabel === 'S' ? fitShortLabel : (toFitLabel === 'L' ? fitLongLabel : ''))
        : '';

      if (this.#headerCells[colIndex]) {
        this.#headerCells[colIndex].textContent = fitSuffix
          ? `${sizeLabel} (${fitSuffix})`
          : sizeLabel;
      }

      for (const field of this.#fields) {
        const cell = this.#bodyCells.get(field)?.[colIndex];
        if (cell) {
          const value = selected[field] ?? '';
          cell.textContent = value && value !== '-' && !this.#noUnitFields.has(field)
              ? `${value} ${this.#currentUnit}`
              : value;
        }
      }
    });
  }
}

if (!customElements.get('size-chart')) {
  customElements.define('size-chart', SizeChart);
}
